import { AccountGroupHierarchyNode } from "@common/types/domains/accounting/account";
import {
    compareAccountsByLeafGroup,
    EMPTY_GROUP_SORT_ORDER_SENTINEL,
    leafGroupHeader,
} from "@common/constants/domains/accounting/accountGroups";
import { buildGroupChain } from "@common/helpers/accounting";
import { resolveMergedAccountSortOrder } from "@common/helpers/domains/accounting/chartRowShaping";
import {
    compareScopeOrder,
    ScopeOrderMember,
} from "@common/helpers/domains/accounting/chartScopeOrdering";
import {
    AccountGroupingIdentity,
    MultiEntityAccount,
} from "./multiEntityAccountGrouping";

export interface MultiEntityAccountGroupNode {
    kind: "group";
    key: string;
    label: string;
    sortOrder: number;
    children: MultiEntityAccountTreeNode[];
}

export interface MultiEntityAccountLeafNode {
    kind: "account";
    key: string;
    label: string;
    sortOrder: number;
    account: MultiEntityAccount;
}

export type MultiEntityAccountTreeNode =
    | MultiEntityAccountGroupNode
    | MultiEntityAccountLeafNode;

export interface MultiEntityAccountTypeNode {
    type: string;
    label: string;
    children: MultiEntityAccountTreeNode[];
}

export interface MultiEntityAccountGroupRow {
    kind: "group";
    key: string;
    label: string;
    depth: number;
}

export interface MultiEntityAccountLeafRow {
    kind: "account";
    key: string;
    account: MultiEntityAccount;
    depth: number;
}

export type MultiEntityAccountRow =
    | MultiEntityAccountGroupRow
    | MultiEntityAccountLeafRow;

export interface MultiEntityAccountSection {
    type: string;
    label: string;
    rows: MultiEntityAccountRow[];
}

interface BuildMultiEntityAccountTreeParams {
    accounts: MultiEntityAccount[];
    identity: AccountGroupingIdentity;
    groupsByEntityId?: Map<number, AccountGroupHierarchyNode[]>;
}

interface FlattenMultiEntityAccountTreeParams {
    tree: MultiEntityAccountTypeNode[];
    search: string;
}

interface TypeNodeBuilder extends MultiEntityAccountTypeNode {
    groupsByKey: Map<string, MultiEntityAccountGroupNode>;
}

const resolveMergedGroupSortOrder = (sortOrders: number[]): number => {
    const storedSortOrders = sortOrders.filter(
        (sortOrder) => sortOrder !== EMPTY_GROUP_SORT_ORDER_SENTINEL,
    );
    return storedSortOrders.length > 0
        ? Math.min(...storedSortOrders)
        : EMPTY_GROUP_SORT_ORDER_SENTINEL;
};

const buildLeafNode = (
    account: MultiEntityAccount,
): MultiEntityAccountLeafNode => ({
    kind: "account",
    key: account.key,
    label: account.name,
    sortOrder: resolveMergedAccountSortOrder(
        account.constituents.map((constituent) => constituent.sortOrder),
    ),
    account,
});

const toScopeMember = (node: MultiEntityAccountTreeNode): ScopeOrderMember => ({
    id: node.key,
    kind: node.kind,
    name: node.label,
    sortOrder: node.sortOrder,
});

const sortTreeNodes = (
    nodes: MultiEntityAccountTreeNode[],
): MultiEntityAccountTreeNode[] =>
    nodes
        .toSorted((first, second) =>
            compareScopeOrder(toScopeMember(first), toScopeMember(second)),
        )
        .map((node) =>
            node.kind === "group"
                ? { ...node, children: sortTreeNodes(node.children) }
                : node,
        );

const getOrCreateTypeNode = (
    typeNodesByType: Map<string, TypeNodeBuilder>,
    account: MultiEntityAccount,
): TypeNodeBuilder => {
    const type = leafGroupHeader.keyOf(account);
    const existing = typeNodesByType.get(type);
    if (existing) {
        return existing;
    }
    const typeNode: TypeNodeBuilder = {
        type,
        label: leafGroupHeader.labelOf(account),
        children: [],
        groupsByKey: new Map(),
    };
    typeNodesByType.set(type, typeNode);
    return typeNode;
};

interface AttachLeafUnderGroupPathParams {
    typeNode: TypeNodeBuilder;
    account: MultiEntityAccount;
    groupsByEntityId: Map<number, AccountGroupHierarchyNode[]>;
}

const attachLeafUnderGroupPath = ({
    typeNode,
    account,
    groupsByEntityId,
}: AttachLeafUnderGroupPathParams): void => {
    const chains = account.constituents.map((constituent) =>
        buildGroupChain(
            groupsByEntityId.get(constituent.entityId) ?? [],
            constituent.parentGroupId,
        ),
    );
    const [leadChain = []] = chains;
    const pathSegments: string[] = [];
    let siblings = typeNode.children;
    leadChain.forEach((group, level) => {
        pathSegments.push(group.name.trim().toLowerCase());
        const key = JSON.stringify([typeNode.type, ...pathSegments]);
        const sortOrder = resolveMergedGroupSortOrder(
            chains.flatMap((chain) =>
                chain.length > level ? [chain[level].sortOrder] : [],
            ),
        );
        const existing = typeNode.groupsByKey.get(key);
        if (existing) {
            existing.sortOrder = Math.min(existing.sortOrder, sortOrder);
            siblings = existing.children;
            return;
        }
        const groupNode: MultiEntityAccountGroupNode = {
            kind: "group",
            key,
            label: group.name,
            sortOrder,
            children: [],
        };
        typeNode.groupsByKey.set(key, groupNode);
        siblings.push(groupNode);
        siblings = groupNode.children;
    });
    siblings.push(buildLeafNode(account));
};

const buildFlatTree = (
    accounts: MultiEntityAccount[],
): MultiEntityAccountTypeNode[] => {
    const typeNodesByType = new Map<string, TypeNodeBuilder>();
    for (const account of accounts.toSorted(compareAccountsByLeafGroup)) {
        getOrCreateTypeNode(typeNodesByType, account).children.push(
            buildLeafNode(account),
        );
    }
    return [...typeNodesByType.values()].map(({ type, label, children }) => ({
        type,
        label,
        children,
    }));
};

const buildGroupedTree = (
    accounts: MultiEntityAccount[],
    groupsByEntityId: Map<number, AccountGroupHierarchyNode[]>,
): MultiEntityAccountTypeNode[] => {
    const typeNodesByType = new Map<string, TypeNodeBuilder>();
    for (const account of accounts) {
        attachLeafUnderGroupPath({
            typeNode: getOrCreateTypeNode(typeNodesByType, account),
            account,
            groupsByEntityId,
        });
    }
    return [...typeNodesByType.values()]
        .toSorted(compareAccountsByLeafGroup)
        .map(({ type, label, children }) => ({
            type,
            label,
            children: sortTreeNodes(children),
        }));
};

export function buildMultiEntityAccountTree({
    accounts,
    identity,
    groupsByEntityId,
}: BuildMultiEntityAccountTreeParams): MultiEntityAccountTypeNode[] {
    return identity === "nameTypePath"
        ? buildGroupedTree(accounts, groupsByEntityId ?? new Map())
        : buildFlatTree(accounts);
}

const matchesAccount = (account: MultiEntityAccount, query: string) =>
    account.name.toLowerCase().includes(query) ||
    account.codeTexts.some((code) => code.toLowerCase().includes(query));

const filterTreeNodes = (
    nodes: MultiEntityAccountTreeNode[],
    query: string,
): MultiEntityAccountTreeNode[] =>
    nodes.flatMap((node): MultiEntityAccountTreeNode[] => {
        if (node.kind === "account") {
            return matchesAccount(node.account, query) ? [node] : [];
        }
        if (node.label.toLowerCase().includes(query)) {
            return [node];
        }
        const children = filterTreeNodes(node.children, query);
        return children.length > 0 ? [{ ...node, children }] : [];
    });

const flattenTreeNodes = (
    nodes: MultiEntityAccountTreeNode[],
    depth: number,
): MultiEntityAccountRow[] => {
    const rows: MultiEntityAccountRow[] = [];
    for (const node of nodes) {
        if (node.kind === "account") {
            rows.push({
                kind: "account",
                key: node.key,
                account: node.account,
                depth,
            });
            continue;
        }
        rows.push({ kind: "group", key: node.key, label: node.label, depth });
        rows.push(...flattenTreeNodes(node.children, depth + 1));
    }
    return rows;
};

export function flattenMultiEntityAccountTree({
    tree,
    search,
}: FlattenMultiEntityAccountTreeParams): MultiEntityAccountSection[] {
    const query = search.trim().toLowerCase();
    return tree.flatMap((typeNode) => {
        const children = query
            ? filterTreeNodes(typeNode.children, query)
            : typeNode.children;
        if (children.length === 0) {
            return [];
        }
        return [
            {
                type: typeNode.type,
                label: typeNode.label,
                rows: flattenTreeNodes(children, 0),
            },
        ];
    });
}
